<script lang="ts">
	import type { OrderProductOptions } from "../../../../core/schema/OrderProductOptions.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { getPriceForProductForCustomerOfSize } from "../../../../core/schema/utils/getPriceForProductForCustomerOfSize.js";
	import ProductConfiguratorOption from "./ProductConfiguratorOption.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import Button from "../Button.svelte";
	import { createEventDispatcher } from "svelte";
	import type { ProductLabel } from "../../../../core/schema/ProductLabel.js";
	import Drawer from "../Drawer/Drawer.svelte";
	import Modal from "../Modal.svelte";
	import standingPrintOnCake from "../../assets/images/stojici-tisk-dort.svg";
	import lyingPrintOnCake from "../../assets/images/lezici-tisk-dort.svg";
	import meringuesOnCake from "../../assets/images/pusinky-dort.svg";
	import labelsOnCake from "../../assets/images/cedulky-dort.svg";
	import numbersOnCake from "../../assets/images/cislo-dort.svg";
	import creamOnCake from "../../assets/images/slehacka-dort.svg";
	import bezlepek from "../../../../core/assets/icons/bezlepek.svg?raw";
	import fillingIcon from "../../../../core/assets/icons/filling.svg?raw";
	import bezlaktoza from "../../../../core/assets/icons/bezlaktoza.svg?raw";
	import ovocenavic from "../../../../core/assets/icons/ovocenavic.svg?raw";
	import pusinky from "../../../../core/assets/icons/pusinky.svg?raw";
	import cislodort from "../../../../core/assets/icons/cislodort.svg?raw";
	import cedulkydort from "../../../../core/assets/icons/cedulkydort.svg?raw";
	import napisdort from "../../../../core/assets/icons/napisdort.svg?raw";
	import photooncake from "../../../../core/assets/icons/photooncake.svg?raw";
	import cakeConf from "../../../../core/assets/icons/cakeconf.svg?raw";
	import macaroonIcon from "../../../../core/assets/icons/makronky.svg?raw";
	import { ProductOption, productOptionKeyMap } from "../../../../core/schema/ProductOption.js";
	import type { ProductPhoto } from "../../../../core/schema/ProductPhoto.js";
	import { getPricesForOptions } from "./getPricesForOptions.js";
	import { minimalOptions } from "../../../../core/schema/productOptionPrices.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { getPriceOfOrderProduct } from "../../../../core/schema/utils/getPriceOfOrderProduct.js";
	import { transformProductToOrderProduct } from "../../../../core/schema/utils/transformProductToOrderProduct.js";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import logoImage from "../../../../core/assets/logo.svg";
	import { appName } from "../../../../core/schema/names/appName.js";
	import type { CreamColor } from "../../../../core/schema/CreamColor.js";
	import Form from "../Form/Form.svelte";
	import type { ProductMacaroons } from "../../../../core/schema/ProductMacaroons.js";
	import { maxMacaroonsPerSize } from "../../../../core/schema/productOptionsConstants.js";
	import { inflectMacaroons } from "./inflectMacaroons.js";
	import type { ProductNumber } from "../../../../core/schema/ProductNumber.js";
	import ProductConfiguratorNumbers from "./ProductConfiguratorNumbers.svelte";
	import ProductConfiguratorLabels from "./ProductConfiguratorLabels.svelte";
	import ProductConfiguratorCreamColor from "./ProductConfiguratorCreamColor.svelte";
	import ProductConfiguratorMacaroons from "./ProductConfiguratorMacaroons.svelte";
	import ProductConfiguratorPhotos from "./ProductConfiguratorPhotos.svelte";
	import ProductOptionDrawer from "./ProductOptionDrawer.svelte";
	import type { OpenableOption } from "./OpenableOption.js";
	import type { ProductSize } from "../../../../core/schema/ProductSize.js";
	import { shouldShowProductForCustomerSize } from "../../../../core/schema/utils/shouldShowProductForCustomerSize.js";
	import { formatNumber } from "../../../../core/utils/formatNumber.js";
	import { ProductSizeLabelType } from "../../../../core/schema/ProductSizeLabelType.js";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";
	import { productSizeLabelTypeShorthandNameMap } from "../../../../core/schema/names/productSizeLabelTypeShorthandNameMap.js";
	import { productSizeNameMap } from "../../../../core/schema/names/productSizeNameMap.js";
	import { doesOrderProductHaveAnyOption } from "../../../../core/schema/utils/doesOrderProductHaveAnyOption.js";
	import { getImagesForProduct } from "../../../../core/schema/utils/getImagesForProduct.js";
	import type { ProductInscription } from "../../../../core/schema/ProductInscription.js";
	import ProductConfiguratorInscription from "./ProductConfiguratorInscription.svelte";
	import type { ProductFilling } from "../../../../core/schema/ProductFilling.js";
	import ProductConfiguratorFilling from "./ProductConfiguratorFilling.svelte";
	import { isProductOptionEnabled } from "../../../../core/schema/utils/isProductOptionEnabled.js";
	import { getNearestDeliveryDateForProduct } from "../../../../core/schema/utils/getNearestDeliveryDate.js";
	import { CustomerType } from "../../../../core/schema/CustomerType.js";
	import { toDateMonthFormat } from "../../../../core/utils/toDateMonthFormat.js";
	import { restrictedProductOptionsForExpress } from "../../../../core/schema/restrictedProductOptionsForExpress.js";
	import { isProductOptionUsed } from "../../../../core/schema/utils/isProductOptionUsed.js";
	import { productOptionNameMap } from "../../../../core/schema/names/productOptionNameMap.js";
	import { emptyOptions } from "../../../../core/schema/utils/emptyOptions.js";
	import { doesProductHaveOption } from "../../../../core/schema/utils/doesProductHaveOption.js";

	export let isOpen: boolean;
	export let product: ProductForCustomer;
	export let size: ProductSize;
	export let options: OrderProductOptions;
	export let openedOption: OpenableOption | undefined = undefined;
	export let isExpress: boolean;
	export let allowExpress: boolean;

	const { TENANT_LOCALE } = getGlobalSettings();
	const dispatch = createEventDispatcher<{
		saveOptions: OrderProductOptions;
	}>();

	let currentOptions = options;
	let lyingPhoto: ProductPhoto | undefined = undefined;
	let creamColor: CreamColor | undefined = undefined;
	let filling: ProductFilling | undefined = undefined;
	let macaroons: ProductMacaroons | undefined = undefined;
	let labels: (ProductLabel & { id: string })[] = [];
	let numbers: ProductNumber[] = [];
	let standingPhotos: { id: string; photo?: ProductPhoto }[] = [];
	let inscription: ProductInscription | undefined = undefined;

	let showRestrictedOptionsWarning = false;
	let usedRestrictedOptions: ProductOption[] = [];
	function toggleExpressVariant(forceOnlyAvailableOptions: boolean): void {
		if (!isExpress) {
			usedRestrictedOptions = [];
			for (const option of restrictedProductOptionsForExpress) {
				const optionKey = productOptionKeyMap[option];
				if (isProductOptionUsed(option, currentOptions[optionKey])) {
					if (forceOnlyAvailableOptions) {
						currentOptions = {
							...currentOptions,
							[optionKey]: emptyOptions[optionKey],
						};
					} else {
						usedRestrictedOptions.push(option);
					}
				}
			}

			if (usedRestrictedOptions.length > 0) {
				showRestrictedOptionsWarning = true;
				return;
			}
			showRestrictedOptionsWarning = false;
		}

		const searchParams = new URLSearchParams(window.location.search);

		isExpress = !isExpress;

		if (isExpress) {
			searchParams.set("express", "");
		} else {
			searchParams.delete("express");
		}

		window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
	}

	$: creamColorEnabled = creamColor !== undefined;
	$: fillingEnabled = filling !== undefined;
	$: inscriptionEnabled = inscription !== undefined;
	$: labelsEnabled = labels.length > 0;
	$: macaroonsEnabled = macaroons !== undefined;
	$: numbersEnabled = numbers.length > 0;
	$: photosEnabled = lyingPhoto !== undefined || standingPhotos.length > 0;

	function initializeOptions(options: OrderProductOptions): void {
		({ lyingPhoto, creamColor, labels, macaroons, numbers, inscription, filling } = options);
		standingPhotos = options.standingPhotos.map((photo) => ({ id: crypto.randomUUID(), photo }));

		creamColorEnabled = creamColor !== undefined;
		fillingEnabled = filling !== undefined;
		inscriptionEnabled = inscription !== undefined;
		labelsEnabled = labels.length > 0;
		macaroonsEnabled = macaroons !== undefined;
		numbersEnabled = numbers.length > 0;
		photosEnabled = lyingPhoto !== undefined || standingPhotos.length > 0;
	}

	initializeOptions(currentOptions);

	let form: HTMLFormElement | null = null;
	function updateCurrentOptions(form: HTMLFormElement): OrderProductOptions {
		const formData = new FormData(form);

		const mappedStandingPhotos = standingPhotos
			.map(({ photo }) => photo)
			.filter((photo): photo is ProductPhoto => photo !== undefined);

		currentOptions = {
			...options,
			creamColor: creamColorEnabled ? creamColor : undefined,
			extraFruit: Boolean(formData.get("extraFruitEnabled")),
			filling,
			glutenFree: Boolean(formData.get("glutenFreeEnabled")),
			inscription,
			labels,
			lactoseFree: Boolean(formData.get("lactoseFreeEnabled")),
			lyingPhoto,
			macaroons: macaroonsEnabled ? macaroons : undefined,
			meringues: Boolean(formData.get("meringuesEnabled")),
			numbers,
			standingPhotos: mappedStandingPhotos,
		};
		return currentOptions;
	}

	let isDirty = false;
	function onFormChange(event?: Event): void {
		event?.stopPropagation();
		if (!form) {
			return;
		}
		isDirty = true;

		updateCurrentOptions(form);
	}

	function removeNumbersChanges(): void {
		numbers = [];
	}
	function removeLabelsChanges(): void {
		labels = [];
	}
	function removeInscriptionChanges(): void {
		inscription = undefined;
	}
	function removeCreamColorChanges(): void {
		creamColor = undefined;
	}
	function removeFillingChanges(): void {
		filling = undefined;
	}
	function removeMacaroonsChanges(): void {
		macaroons = undefined;
	}
	function removePhotosChanges(): void {
		lyingPhoto = undefined;
		standingPhotos = [];
	}

	let showCloseModal = false;
	function onCancel(): void {
		isOpen = false;
		openedOption = undefined;
	}

	function onContinue(): void {
		showCloseModal = false;
	}

	function onClose(): boolean {
		if (!isDirty) {
			openedOption = undefined;
			return true;
		}

		showCloseModal = true;
		return false;
	}

	function onOpenChanged(open: boolean): void {
		if (open) {
			currentOptions = options;
			initializeOptions(currentOptions);
		} else {
			showCloseModal = false;
			isDirty = false;
		}
	}

	$: onOpenChanged(isOpen);

	$: basePrice = getPriceForProductForCustomerOfSize(product, size)?.withTax ?? 0;
	$: minimalPricesForOptions = getPricesForOptions(basePrice, minimalOptions);
	$: pricesForOptions = getPricesForOptions(basePrice, currentOptions);
	$: totalPrice = getPriceOfOrderProduct(
		transformProductToOrderProduct(crypto.randomUUID(), product, 1, size, currentOptions),
	);

	$: nearestDeliveryDate = getNearestDeliveryDateForProduct(product, CustomerType.B2c, false);

	function submit(): void {
		if (!form?.reportValidity()) {
			return;
		}

		const newOptions = updateCurrentOptions(form);
		dispatch("saveOptions", newOptions);
	}

	function updateNumbers(newNumbers: ProductNumber[]): void {
		if (!form?.reportValidity()) {
			return;
		}
		numbers = newNumbers;
		openedOption = undefined;
		onFormChange();
	}
	function updateLabels(newLabels: ProductLabel[]): void {
		if (!form?.reportValidity()) {
			return;
		}
		labels = newLabels;
		openedOption = undefined;
		onFormChange();
	}
	function updateInscription(newInscription: ProductInscription): void {
		if (!form?.reportValidity()) {
			return;
		}
		if (newInscription.text) {
			inscription = newInscription;
		} else {
			inscription = undefined;
		}
		openedOption = undefined;
		onFormChange();
	}
	function updateCreamColor(newCreamColor: CreamColor | undefined): void {
		if (!form?.reportValidity()) {
			return;
		}
		creamColor = newCreamColor;
		openedOption = undefined;
		onFormChange();
	}
	function updateFilling(newFilling: ProductFilling | undefined): void {
		if (!form?.reportValidity()) {
			return;
		}
		filling = newFilling;
		openedOption = undefined;
		onFormChange();
	}
	function updateMacaroons(newMacaroons: ProductMacaroons | undefined): void {
		if (!form?.reportValidity()) {
			return;
		}
		macaroons = newMacaroons;
		openedOption = undefined;
		onFormChange();
	}
	function updatePhotos(
		newLyingPhoto: ProductPhoto | undefined,
		newStandingPhotos: { id: string; photo?: ProductPhoto }[],
	): void {
		if (!form?.reportValidity()) {
			return;
		}
		lyingPhoto = newLyingPhoto;
		standingPhotos = newStandingPhotos;
		openedOption = undefined;
		onFormChange();
	}

	let changeMap: Partial<Record<OpenableOption, boolean>> = {};
	$: {
		if (openedOption === undefined) {
			changeMap = {};
		}
	}

	$: customSize = product.sizeLabel && product.sizeLabel.type !== ProductSizeLabelType.Serving;
</script>

<Modal bind:show={showRestrictedOptionsWarning} class="max-w-[36.5rem] !p-4 sm:!p-8">
	<div class="text-center">
		<span>Následující příplatky nelze objednat v expresním režimu:</span>
		<ul class="mt-2">
			{#each usedRestrictedOptions as option}
				<li class="ml-5 list-disc text-left sm:ml-[5rem]">{productOptionNameMap[option][TENANT_LOCALE]}</li>
			{/each}
		</ul>
	</div>

	<div slot="actions" class="flex flex-wrap items-center justify-between gap-2 sm:gap-0">
		<Button
			class="border-0"
			variant="secondary"
			on:click={() => {
				showRestrictedOptionsWarning = false;
			}}
		>
			Ponechat příplatky
		</Button>
		<Button
			variant="primary"
			on:click={() => {
				toggleExpressVariant(true);
			}}
		>
			Chci odstranit nedostupné příplatky
		</Button>
	</div>
</Modal>

<Modal bind:show={showCloseModal} class="max-w-[27.5rem] !p-4 sm:!p-8">
	<div class="text-center">
		<span>Zavřením panelu zrušíte všechny úpravy na dortu a budete pak muset začít znovu.</span>
	</div>

	<div slot="actions" class="flex flex-wrap items-center justify-between gap-2 sm:gap-0">
		<Button class="border-0" variant="secondary" on:click={onCancel}>Zrušit úpravy</Button>
		<Button variant="secondary" on:click={onContinue}>Pokračovat v úpravách</Button>
	</div>
</Modal>

<Drawer
	noPadding
	bind:isOpen
	class="w-full sm:w-[32rem] {openedOption !== undefined &&
		'mr-[12.625rem] delay-[250ms]'} transition-all duration-200 ease-out"
	position="right"
	{onClose}
>
	{@const [primaryImage] = getImagesForProduct(product, size)}

	<Form
		bind:form
		class="flex h-full w-full flex-col pb-[11.5rem] pt-7"
		on:change={onFormChange}
		on:input={onFormChange}
	>
		<div class="flex w-full flex-col px-4 sm:px-8 md:px-14">
			<p class="text-md mb-6 font-semibold sm:text-lg">Upravit</p>
			<div class="flex w-full justify-between">
				<div
					class="grid w-full grid-cols-1 grid-rows-1 gap-x-3 gap-y-1 sm:grid-cols-[5.5rem,auto] sm:gap-x-6 sm:gap-y-2"
				>
					<div class="flex gap-2">
						{#if primaryImage}
							<DynamicPicture
								image={primaryImage}
								width={80}
								height={80}
								bucket="storage"
								class="row-span-3"
								imgClass="rounded-lg h-[88px] w-[88px] object-contain"
								alt={product.name[TENANT_LOCALE]}
								loading="eager"
							/>
						{:else}
							<div class="bg-secondary-40 row-span-3 flex h-24 items-center justify-center rounded-lg sm:h-[7.5rem]">
								<StaticPicture
									alt="Logo {appName}"
									image={logoImage}
									class="h-16 w-16 sm:h-20 sm:w-20"
									width={80}
									height={80}
									loading="eager"
								/>
							</div>
						{/if}
						<p class="text-base font-semibold sm:hidden">
							{product.name[TENANT_LOCALE]}
						</p>
					</div>
					<div class="flex flex-col justify-between">
						<p class="text-md hidden font-semibold sm:inline">
							{product.name[TENANT_LOCALE]}
						</p>
						<div class="text-2xs mt-2 flex w-full items-center justify-between sm:mt-0 sm:text-xs">
							<div
								class="flex flex-wrap items-center gap-x-3"
								class:hidden={!shouldShowProductForCustomerSize(product)}
							>
								<span>Velikost</span>
								<span
									class="border-secondary text-secondary text-3xs sm:text-2xs flex h-6 cursor-default select-none items-center justify-center truncate rounded-full border bg-white py-2 font-semibold uppercase leading-3 transition-all duration-300 sm:h-7"
									class:aspect-square={!customSize}
									class:px-2={customSize}
								>
									{#if product.sizeLabel && product.sizeLabel.type !== ProductSizeLabelType.Serving}
										<div class="flex gap-0.5">
											<span>
												{formatNumber(product.sizeLabel.values[productSizeKeyMap[size]], TENANT_LOCALE, false)}
											</span>
											<span>{productSizeLabelTypeShorthandNameMap[product.sizeLabel.type]}</span>
										</div>
									{:else}
										{productSizeNameMap[size][TENANT_LOCALE]}
									{/if}
								</span>
							</div>
							<p>
								Základní cena: <span class="font-semibold">{formatPrice(basePrice, TENANT_LOCALE, false)}</span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mt-6 flex flex-1 flex-col gap-2 overflow-y-auto px-4 sm:px-8 md:px-14">
			{#if isProductOptionEnabled(ProductOption.GlutenFreeVariant, product.enabledOptions, isExpress)}
				<ProductConfiguratorOption
					id="glutenFreeEnabled"
					name="Bezlepková varianta"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.glutenFree}
					icon={bezlepek}
					tooltipCharacter="!"
					productOption={ProductOption.GlutenFreeVariant}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Počítejte, prosím, s možnými stopami lepku, protože vše vyrábíme pod jednou střechou.
					</div>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.LactoseFreeVariant, product.enabledOptions, isExpress)}
				<ProductConfiguratorOption
					id="lactoseFreeEnabled"
					name="Bezlaktózová varianta"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.lactoseFree}
					icon={bezlaktoza}
					tooltipCharacter="!"
					productOption={ProductOption.LactoseFreeVariant}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Počítejte, prosím, s možnými stopami laktózy, protože vše vyrábíme pod jednou střechou.
					</div>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.ExtraFruit, product.enabledOptions, isExpress)}
				<ProductConfiguratorOption
					id="extraFruitEnabled"
					name="Ovoce navíc"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.extraFruit}
					icon={ovocenavic}
					productOption={ProductOption.ExtraFruit}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">Dort vám bohatě přizdobíme krásnými jahodami, malinami a borůvkami.</div>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.Numbers, product.enabledOptions, isExpress)}
				{@const productOption = ProductOption.Numbers}
				<ProductConfiguratorOption
					id="numbersEnabled"
					name="Čokoládové číslice"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={cislodort}
					tooltipImages={[{ src: numbersOnCake, alt: "Vizualizace čísla na dortu" }]}
					bind:openedOption
					{productOption}
					cancelChanges={removeNumbersChanges}
					enabled={numbersEnabled}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Používáme čokoládové číslice z bílé nebo hořké čokolády, které si sami vyrábíme. Naše cukrářky zvolí daný
						druh číslic s ohledem na to, aby s vaším dortem ladily.
					</div>
					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removeNumbersChanges}
						{changeMap}
					>
						<ProductConfiguratorNumbers
							bind:changed={changeMap[productOption]}
							{numbers}
							{updateNumbers}
							{minimalPricesForOptions}
							{basePrice}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.Labels, product.enabledOptions, isExpress)}
				{@const productOption = ProductOption.Labels}
				<ProductConfiguratorOption
					id="labelEnabled"
					name="Přidat cedulku"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={cedulkydort}
					tooltipImages={[{ src: labelsOnCake, alt: "Vizualizace cedulek na dortu" }]}
					bind:openedOption
					{productOption}
					cancelChanges={removeLabelsChanges}
					enabled={labelsEnabled}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Ručně psané jméno nebo krátký vzkaz na našich čokoládových destičkách z dortu udělá zároveň i krásné gesto.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removeLabelsChanges}
						{changeMap}
					>
						<ProductConfiguratorLabels
							bind:changed={changeMap[productOption]}
							{minimalPricesForOptions}
							{labels}
							{updateLabels}
							{basePrice}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.Inscription, product.enabledOptions, isExpress)}
				{@const productOption = ProductOption.Inscription}
				<ProductConfiguratorOption
					id="inscriptionEnabled"
					name="Přidat nápis"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={napisdort}
					bind:openedOption
					{productOption}
					cancelChanges={removeInscriptionChanges}
					enabled={inscriptionEnabled}
					minimalPriceIsFinal
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">Ručně psané jméno nebo krátký vzkaz udělá z dortu osobní záležitost.</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removeInscriptionChanges}
						{changeMap}
					>
						<ProductConfiguratorInscription
							bind:changed={changeMap[productOption]}
							{minimalPricesForOptions}
							{inscription}
							{updateInscription}
							{basePrice}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.Filling, product.enabledOptions, isExpress)}
				{@const productOption = ProductOption.Filling}
				<ProductConfiguratorOption
					id="fillingEnabled"
					name="Náplň"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={fillingIcon}
					bind:openedOption
					{productOption}
					cancelChanges={removeFillingChanges}
					enabled={fillingEnabled}
					minimalPriceIsFinal
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Vyberte si náplň Vašeho dortu. S našimi cukráři jsme vyladili chutě všech možností, proto s žádnou volbou
						nešlápnete vedle.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removeFillingChanges}
						{changeMap}
					>
						<ProductConfiguratorFilling
							bind:changed={changeMap[productOption]}
							{minimalPricesForOptions}
							{basePrice}
							{filling}
							{updateFilling}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.LyingPhoto, product.enabledOptions, isExpress) || isProductOptionEnabled(ProductOption.StandingPhotos, product.enabledOptions, isExpress)}
				{@const productOption = "photos"}
				<ProductConfiguratorOption
					id="photosEnabled"
					name="Jedlý tisk"
					icon={photooncake}
					{minimalPricesForOptions}
					{pricesForOptions}
					tooltipImages={[
						{ src: lyingPrintOnCake, alt: "Vizualizace ležícího tisku na dortu" },
						{ src: standingPrintOnCake, alt: "Vizualizace stojícího tisku na dortu" },
					]}
					bind:openedOption
					{productOption}
					cancelChanges={removePhotosChanges}
					enabled={photosEnabled}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Nahrajte obrázky, které si přejete vytisknout. Pro nejlepší efekt volte vysokou kvalitu. Jedlý papír je
						bezlepkový.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removePhotosChanges}
						{changeMap}
					>
						<ProductConfiguratorPhotos
							bind:changed={changeMap[productOption]}
							{minimalPricesForOptions}
							{basePrice}
							allowLying={doesProductHaveOption(product.enabledOptions, ProductOption.LyingPhoto)}
							allowStanding={doesProductHaveOption(product.enabledOptions, ProductOption.StandingPhotos)}
							{lyingPhoto}
							{standingPhotos}
							{updatePhotos}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.Meringues, product.enabledOptions, isExpress)}
				<ProductConfiguratorOption
					id="meringuesEnabled"
					name="Přidat sněhové pusinky"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.meringues}
					icon={pusinky}
					tooltipImages={[{ src: meringuesOnCake, alt: "Vizualizace pusinek na dortu" }]}
					productOption={ProductOption.Meringues}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">Několik barevných pusinek a dort je hned veselejší!</div>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.CreamColor, product.enabledOptions, isExpress)}
				{@const productOption = ProductOption.CreamColor}
				<ProductConfiguratorOption
					id="creamColorEnabled"
					name="Pokrýt barevnou šlehačkou"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={cakeConf}
					tooltipImages={[{ src: creamOnCake, alt: "Vizualizace dortu pokrytého šlehačkou" }]}
					bind:openedOption
					{productOption}
					cancelChanges={removeCreamColorChanges}
					enabled={creamColorEnabled}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Dort obmažeme nahoře i po bocích vámi vybranou barvou. Ideální volba pro kombinaci s jedlým tiskem.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removeCreamColorChanges}
						{changeMap}
					>
						<ProductConfiguratorCreamColor
							bind:changed={changeMap[productOption]}
							{minimalPricesForOptions}
							{basePrice}
							{creamColor}
							{updateCreamColor}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if isProductOptionEnabled(ProductOption.Macaroons, product.enabledOptions, isExpress)}
				{@const productOption = ProductOption.Macaroons}
				{@const max = maxMacaroonsPerSize[size]}
				<ProductConfiguratorOption
					id="macaroonsEnabled"
					name="Makronky"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={macaroonIcon}
					bind:openedOption
					{productOption}
					cancelChanges={removeMacaroonsChanges}
					enabled={macaroonsEnabled}
					{product}
					selectedSize={size}
				>
					<div slot="tooltip">
						Přidej si na svůj dort až {max}
						{inflectMacaroons(max)} podle své chuti.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						{productOption}
						removeChanges={removeMacaroonsChanges}
						{changeMap}
					>
						<ProductConfiguratorMacaroons
							bind:changed={changeMap[productOption]}
							{minimalPricesForOptions}
							{basePrice}
							{macaroons}
							{updateMacaroons}
							{size}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}
			{#if nearestDeliveryDate && allowExpress}
				<div
					class="border-grey-200 flex w-full flex-col items-center justify-start gap-2 rounded-lg border px-[calc(.5rem+1px)] py-[calc(.75rem+1px)] sm:p-[calc(.75rem+1px)]"
				>
					<div class="flex flex-1 items-center gap-2">
						<div>
							{#if isExpress}
								Přejete si více úprav? Přejděte na standardní produkt
							{:else}
								K doručení nejdříve {toDateMonthFormat(nearestDeliveryDate, TENANT_LOCALE)}
							{/if}
						</div>
						<span class="flex min-w-fit flex-1 justify-end text-xs transition-all duration-200 sm:text-base">
							<Button
								class="h-min min-w-fit"
								variant="secondary"
								on:click={() => {
									toggleExpressVariant(false);
								}}
							>
								{#if isExpress}
									Více úprav
								{:else}
									Chci to už zítra
								{/if}
							</Button>
						</span>
					</div>
				</div>
			{/if}
		</div>

		<div
			class="absolute bottom-0 left-0 h-[10.625rem] w-full rounded-t-[22px] bg-white p-6 shadow-[0_-6px_8px_-5px_rgba(225,225,225,225)]"
		>
			<div class="text-md flex items-end justify-between font-semibold sm:text-lg">
				<span class="text-md">Celková cena</span>
				<span class="text-lg">{nbspify(formatPrice(totalPrice.withTax, TENANT_LOCALE, false))}</span>
			</div>

			<Button
				class="mt-4 w-full justify-center !rounded-[1.875rem] py-[.875rem] text-[1.125rem] leading-6 sm:py-[1.063rem] md:text-[1.375rem]"
				icon={cake}
				iconClass="w-6"
				variant="primary"
				on:click={submit}
			>
				{#if !doesOrderProductHaveAnyOption(options)}
					Přidat úpravy
				{:else}
					Změnit úpravy
				{/if}
			</Button>
		</div>
	</Form>
</Drawer>
